import {functions, httpsCallable} from "@/firebase/firebaseConfig";
import {getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem} from "@/lib/storageUtils";

export interface MintDesktopCustomTokenResponse {
    customToken: string;
}

// Callable names must match the deployed Cloud Function. Override with an env var if needed.
const DESKTOP_AUTH_MINT_CALLABLE = process.env.NEXT_PUBLIC_DESKTOP_AUTH_MINT_CALLABLE ?? "auth-mintDesktopCustomToken";
const DESKTOP_AUTH_REDIRECT_BASES: Record<string, string> = {
    desktop_app: process.env.NEXT_PUBLIC_DESKTOP_APP_AUTH_REDIRECT_BASE ?? "braiv-compressor://auth",
    desktop_capture: process.env.NEXT_PUBLIC_DESKTOP_CAPTURE_AUTH_REDIRECT_BASE ?? "braiv-capture://auth",
};
// iOS Google in-app browsers can expose `window.localStorage === null`.
// Every read, write, and delete goes through storageUtils so that stays a no-op.
const DESKTOP_AUTH_STORAGE_KEY = "braiv.desktopAuth.pending";
const DESKTOP_AUTH_TTL_MS = 24 * 60 * 60 * 1000;

function getSearchParams(search?: string): URLSearchParams {
    if (!search) {
        return new URLSearchParams();
    }
    return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
}

function hasCustomToken(data: unknown): data is MintDesktopCustomTokenResponse {
    return typeof data === "object"
        && data !== null
        && typeof (data as {customToken?: unknown}).customToken === "string"
        && (data as {customToken: string}).customToken.length > 0;
}

export function isDesktopAppAuthRequest(search?: string): boolean {
    return getDesktopAuthClient(search) !== null;
}

export async function mintDesktopCustomToken(): Promise<string> {
    const mintFn = httpsCallable<Record<string, never>, MintDesktopCustomTokenResponse>(
        functions,
        DESKTOP_AUTH_MINT_CALLABLE
    );
    const result = await mintFn({});

    if (!hasCustomToken(result.data)) {
        throw new Error("invalid-desktop-custom-token-response");
    }

    return result.data.customToken;
}

export function buildDesktopAppAuthRedirectUrl(customToken: string, search?: string): string {
    const client = getDesktopAuthClient(search);
    const redirectBase = normalizeRedirectBase(
        client ? DESKTOP_AUTH_REDIRECT_BASES[client] : DESKTOP_AUTH_REDIRECT_BASES.desktop_app
    );
    const separator = redirectBase.includes("?") ? "&" : "?";
    return `${redirectBase}${separator}token=${encodeURIComponent(customToken)}`;
}

export async function completeDesktopAppAuthHandoff(): Promise<void> {
    const customToken = await mintDesktopCustomToken();
    const redirectUrl = buildDesktopAppAuthRedirectUrl(customToken);
    clearDesktopAuthClient();
    window.location.href = redirectUrl;
}

/** Client saved from the signup or login query, if it is still within the 24-hour window. */
export function pendingDesktopAuthClient(): string | null {
    return readPendingDesktopAuthClient();
}

/** Remember `client=desktop_capture` (or compressor) so a later tab can finish the handoff. */
export function rememberDesktopAuthClient(search?: string): void {
    const searchToRead = search ?? (typeof window !== "undefined" ? window.location.search : "");
    const client = clientFromSearch(searchToRead);
    if (!client || typeof window === "undefined") {
        return;
    }
    setLocalStorageItem(DESKTOP_AUTH_STORAGE_KEY, JSON.stringify({
        client,
        createdAt: Date.now(),
    }));
}

export function clearDesktopAuthClient(): void {
    removeLocalStorageItem(DESKTOP_AUTH_STORAGE_KEY);
}

export function getDesktopAuthFailureKey(error: unknown): string {
    const code = String((error as {code?: string} | null | undefined)?.code || "").toLowerCase();
    const message = String((error as {message?: string} | null | undefined)?.message || "").toLowerCase();

    if (code.includes("unauthenticated") || message.includes("unauthenticated")) {
        return "errors.desktopAuthUnauthenticated";
    }

    if (code.includes("unavailable") || code.includes("network") || message.includes("network")) {
        return "errors.desktopAuthNetwork";
    }

    return "errors.desktopAuthFailed";
}

function getDesktopAuthClient(search?: string): string | null {
    const searchToRead = search ?? (typeof window !== "undefined" ? window.location.search : "");
    const client = clientFromSearch(searchToRead);
    if (client) {
        rememberDesktopAuthClient(searchToRead);
        return client;
    }
    return readPendingDesktopAuthClient();
}

function clientFromSearch(search?: string): string | null {
    const client = getSearchParams(search).get("client");
    if (!client || !(client in DESKTOP_AUTH_REDIRECT_BASES)) {
        return null;
    }
    return client;
}

function readPendingDesktopAuthClient(): string | null {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const raw = getLocalStorageItem(DESKTOP_AUTH_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as {client?: unknown; createdAt?: unknown};
        const client = typeof parsed.client === "string" ? parsed.client : "";
        const createdAt = typeof parsed.createdAt === "number" ? parsed.createdAt : 0;
        if (!(client in DESKTOP_AUTH_REDIRECT_BASES) || Date.now() - createdAt > DESKTOP_AUTH_TTL_MS) {
            removeLocalStorageItem(DESKTOP_AUTH_STORAGE_KEY);
            return null;
        }
        return client;
    } catch {
        // Corrupt payloads are dropped. Removal must not throw when storage is
        // null or blocked: that second throw rejected completeOnboardingAndContinue
        // (JAVASCRIPT-NEXTJS-1N4) and the Capture setup banner (JAVASCRIPT-NEXTJS-1N5).
        removeLocalStorageItem(DESKTOP_AUTH_STORAGE_KEY);
        return null;
    }
}

function normalizeRedirectBase(redirectBase: string): string {
    return redirectBase.replace(/\/+$/, "");
}

/** @deprecated Use getDesktopAuthFailureKey with useTranslations */
export function getDesktopAuthFailureMessage(error: unknown): string {
    return getDesktopAuthFailureKey(error);
}
